<template>
  <b-card
    v-if="!!option"
    id="page-charge-form"
    data-tour="charge-form-page"
    no-body
    class="mb-0"
  >
    <b-card-header>
      <b-card-title
        ><h2>
          {{ $t(getTitle()) }} {{ $t("pages.charges.title").toLowerCase() }}
        </h2></b-card-title
      >
      <div class="d-flex align-items-center flex-wrap justify-content-end">
        <tutorial-button
          data-tour="charge-form-help"
          class="mr-1 mb-50"
          :label="$t('tutorials.common.viewTutorial')"
          :disabled="loading"
          @click="startTutorial"
        />
        <div
          data-tour="charge-form-back"
          class="mb-50"
        >
          <b-button variant="primary" @click="goBack">
            <feather-icon icon="ArrowLeftIcon" />
            {{ $t("back") }}
          </b-button>
        </div>
      </div>
    </b-card-header>

    <b-card-body>
      <validation-observer ref="simpleRules">
        <b-form>
          <b-row>
            <b-col
              cols="12"
              md="6"
              data-tour="charge-form-name"
            >
              <b-form-group :label="$t(label('name'))" label-for="name">
                <validation-provider
                  #default="{ errors }"
                  :name="$t(label('name'))"
                  rules="required"
                >
                  <b-form-input
                    id="name"
                    v-model="charge.name"
                    :state="errors.length > 0 ? false : null"
                    :placeholder="$t(label('name'))"
                    :disabled="!isSaveMode"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col
              cols="12"
              md="6"
              data-tour="charge-form-description"
            >
              <b-form-group
                :label="$t(label('description'))"
                label-for="description"
              >
                <b-form-textarea
                  id="description"
                  v-model="charge.description"
                  :placeholder="$t(label('description'))"
                  :disabled="!isSaveMode"
                  rows="6"
                />
              </b-form-group>
            </b-col>
            <b-col
              v-if="option != 2"
              data-tour="charge-form-actions"
              class="d-flex justify-content-end align-items-center"
              cols="12"
            >
              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                type="submit"
                variant="primary"
                class="mr-1"
                @click.prevent="onSubmit"
              >
                {{ $t("save") }}
              </b-button>
              <b-button
                v-if="option == 1"
                v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                type="reset"
                variant="outline-secondary"
                @click="onCancel"
              >
                {{ $t("cancel") }}
              </b-button>
            </b-col>
          </b-row>
        </b-form>
      </validation-observer>
    </b-card-body>

    <app-tour
      :name="tutorialName"
      :steps="tutorialSteps"
      :options="tutorialOptions"
      :callbacks="tutorialCallbacks"
      @target-not-found="onTutorialTargetNotFound"
    />
  </b-card>
</template>

<script>
import _ from "lodash";
import {
  ValidationProvider,
  ValidationObserver,
  localize,
  extend,
} from "vee-validate";
import {
  BCard,
  BCardHeader,
  BCardTitle,
  BCardBody,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BForm,
  BFormTextarea,
  BAlert,
  BFormInvalidFeedback,
  BFormGroup,
} from "bootstrap-vue";
import router from "@/router";
import Ripple from "vue-ripple-directive";
import { required } from "@validations";
import { showLoading, stopLoading } from "@core/utils/utils";
import AppTour from "@core/components/app-tour/AppTour.vue";
import TutorialButton from "@core/components/app-tour/TutorialButton.vue";

import { getCharge, createOrEditCharge } from "@core/api/request/charges";
import { buildCustomerChargeFormTour } from "@/tutorials/customerCharges";

const charge = {
  idRol: null,
  guidCompany: null,
  name: "",
  description: "",
};

export default {
  components: {
    BAlert,
    ValidationProvider,
    ValidationObserver,
    BCard,
    BCardHeader,
    BCardTitle,
    BCardBody,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BForm,
    BFormTextarea,
    BFormInvalidFeedback,
    BFormGroup,
    AppTour,
    TutorialButton,
  },
  directives: {
    Ripple,
  },
  data: () => ({
    loading: false,
    isSaveMode: true,
    required,
    option: "",
    charge: Object.assign(charge),
    tutorialName: "customerChargeFormTour",
  }),
  computed: {
    tutorialMode() {
      return {
        1: "create",
        2: "show",
        3: "edit",
      }[this.option];
    },
    tutorialSteps() {
      if (!this.tutorialMode) return [];

      return buildCustomerChargeFormTour({
        t: this.$t.bind(this),
        mode: this.tutorialMode,
      });
    },
    tutorialOptions() {
      return {
        highlight: true,
        enableScrolling: true,
        useKeyboardNavigation: true,
        startTimeout: 150,
        stopOnTargetNotFound: true,
      };
    },
    tutorialCallbacks() {
      return {
        onFinish: this.onTutorialFinish,
      };
    },
  },

  created() {
    localize("es");
    this.initialize();
  },

  watch: {
    "$store.state.appConfig.activeCompanyGuid"(guid) {
      this.charge.guidCompany = guid;
    },
  },

  mounted() {},

  methods: {
    async startTutorial() {
      if (this.loading) return;

      await this.$nextTick();

      const tour = this.$tours[this.tutorialName];
      if (!tour || !this.tutorialSteps.length) return;

      const allTargetsExist = this.tutorialSteps.every((step) =>
        document.querySelector(step.target),
      );

      if (!allTargetsExist) {
        this.onTutorialTargetNotFound();
        return;
      }

      try {
        await tour.start();
      } catch (error) {
        this.onTutorialTargetNotFound();
      }
    },
    onTutorialFinish() {
      const idUser =
        this.$store.state.appConfig.sessionContext?.idUser || "user";
      localStorage.setItem(
        `sofia:tutorial:${idUser}:customerCharges:form:${this.tutorialMode}:v1`,
        "completed",
      );
    },
    onTutorialTargetNotFound() {
      this.alertToast(
        "warning",
        this.$t("tutorials.common.unavailableTitle"),
        this.$t("tutorials.common.unavailableContent"),
        "AlertTriangleIcon",
      );
    },
    label(field) {
      return `pages.charges.form.label.${field}`;
    },

    labelHeader(field) {
      return `pages.charges.table.tableHeader.${field}`;
    },

    getTitle() {
      if (this.option === 1) return "register";
      else if (this.option === 2) return "show";
      else if (this.option === 3) return "edit";
    },

    async initialize() {
      this.option = this.$route.params.option;

      if (this.option === undefined) {
        router.push({ name: "charges" }).catch(() => {});
      }

      switch (this.option) {
        case 1:
          this.onCancel();
          this.charge.guidCompany =
            this.$store.state.appConfig.activeCompanyGuid;
          break;
        case 2:
        case 3:
          if (this.$route.params.idRol === undefined) {
            router.push({ name: "charges" }).catch(() => {});
          }
          this.isSaveMode = this.option !== 2;
          await this.showCharge(this.$route.params.idRol);
          break;
      }
    },

    onSubmit() {
      try {
        this.$refs.simpleRules.validate().then((success) => {
          if (success) {
            showLoading();

            const body = {
              idRol: this.charge.idRol || null,
              guidCompany: this.charge.guidCompany,
              name: this.charge.name,
              description: this.charge.description,
            };

            const idRol = this.option === 1 ? null : this.charge.idRol;
            createOrEditCharge(body, idRol)
              .then(() => {
                this.alertToast(
                  "success",
                  this.$t("Alert"),
                  `${
                    this.option != 3
                      ? this.$t("registerSuccess")
                      : this.$t("updateRecord")
                  }`,
                  "CheckIcon",
                );
                this.goBack();
                stopLoading();
              })
              .catch((error) => {
                console.error("Error loading data", error.message);
                stopLoading();
                this.alertToast(
                  "danger",
                  this.$t("Alert"),
                  error.message,
                  "AlertTriangleIcon",
                );
              });
          }
        });
      } catch (error) {
        console.error("Error loading data", error.message);
        this.alertToast("danger", "Error", error.message, "AlertTriangleIcon");
      }
    },

    showCharge(idRol) {
      this.loading = true;
      showLoading();
      getCharge(idRol)
        .then((resp) => {
          this.loading = false;
          stopLoading();
          const charge = _.get(resp, "data");

          this.charge = Object.assign(this.charge, {
            idRol: charge.idRol,
            guidCompany: charge.guidCompany || null,
            name: charge.name || "",
            description: charge.description || "",
          });
        })
        .catch((error) => {
          this.loading = false;
          console.log("error", error);
          stopLoading();
          this.alertToast(
            "danger",
            this.$t("Alert"),
            error.message,
            "AlertTriangleIcon",
          );
        });
    },

    goBack() {
      this.$refs.simpleRules.reset();
      router.back();
    },

    onCancel() {
      Object.assign(this.charge, {
        idRol: null,
        guidCompany: null,
        name: "",
        description: "",
      });
    },

    message(title, text, icon) {
      this.$swal({
        title,
        text,
        icon,
        customClass: {
          confirmButton: "btn btn-success",
        },
      });
    },

    alertToast(type = null, title = null, message = null, icon) {
      this.$bvToast.toast(message, {
        title,
        variant: type,
        solid: true,
        icon,
      });
    },
  },
};
</script>

<style lang="scss"></style>
